/**
 * Renders the inner markup of a maker page (everything inside .ms-root).
 * Shared verbatim by the Convex SSR action and the editor preview pane.
 */

import type { PublicPage, RenderOptions } from "./types"
import { escapeHtml, escapeAttr, safeUrl, renderMarkdown, socialIconSvg } from "./html"

const SOCIAL_ORDER = ["twitter", "github", "tiktok", "instagram", "youtube", "linkedin", "email"] as const

function socialHref(platform: string, value: string): string {
  if (platform === "email") return `mailto:${value}`
  return safeUrl(value)
}

function startupStatusClass(status: string): string {
  const normalized = status.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
  return normalized || "unknown"
}

function renderStartup(s: PublicPage["startups"][number], preview: boolean): string {
  const destination = safeUrl(s.url)
  const isLinked = preview || (Boolean(s.url) && destination !== "#")
  const href = preview ? "#" : escapeAttr(destination)
  const target = preview ? "" : ' target="_blank" rel="noopener"'
  const icon = s.iconUrl
    ? `<img class="ms-card-icon" src="${escapeAttr(s.iconUrl)}" alt="" loading="lazy"/>`
    : ""
  const status = s.status
    ? `<span class="ms-status ms-status-${startupStatusClass(s.status)}">${escapeHtml(s.status)}</span>`
    : ""
  const revenue = s.revenue ? `<span class="ms-card-revenue">${escapeHtml(s.revenue)}</span>` : ""
  const tagline = s.tagline ? `<div class="ms-card-tagline">${escapeHtml(s.tagline)}</div>` : ""
  const track = !preview && s.id ? ` data-ms-startup="${escapeAttr(s.id)}"` : ""
  const content = `${icon}
  <div class="ms-card-body">
    <div class="ms-card-title-row">
      <div class="ms-card-name">${escapeHtml(s.name)}</div>
      <div class="ms-card-meta">${revenue}${status}</div>
    </div>
    ${tagline}
  </div>`

  return isLinked
    ? `<a class="ms-card" href="${href}"${target}${track}>${content}</a>`
    : `<div class="ms-card ms-card-static">${content}</div>`
}

export function renderPageBody(page: PublicPage, opts?: Partial<RenderOptions>): string {
  const preview = opts?.preview ?? false

  const avatar = page.avatarUrl
    ? `<img class="ms-avatar" src="${escapeAttr(page.avatarUrl)}" alt="${escapeAttr(page.displayName)}"/>`
    : `<div class="ms-avatar"></div>`

  const location = page.location
    ? `<span class="ms-location">📍 ${escapeHtml(page.location)}</span>`
    : ""

  const bio = page.bio ? `<p class="ms-bio">${renderMarkdown(page.bio)}</p>` : ""

  const socials = SOCIAL_ORDER.filter((p) => page.social[p])
    .map(
      (p) =>
        `<a href="${escapeAttr(socialHref(p, page.social[p]!))}"${
          preview ? "" : ' target="_blank" rel="noopener"'
        } aria-label="${p}">${socialIconSvg(p)}</a>`,
    )
    .join("")
  const socialsBlock = socials ? `<div class="ms-socials">${socials}</div>` : ""

  const subscribe = page.emailCollection.enabled
    ? `<div class="ms-subscribe">
    <p class="ms-subscribe-headline">${escapeHtml(page.emailCollection.headline || "Subscribe for updates")}</p>
    <form class="ms-subscribe-row" data-ms-subscribe="${escapeAttr(page.username)}"${preview ? " onsubmit=\"return false\"" : ""}>
      <input type="email" name="email" placeholder="you@example.com" required${preview ? " disabled" : ""}/>
      <button type="submit">Subscribe</button>
    </form>
  </div>`
    : ""

  const startups = page.startups.length
    ? `<div class="ms-startups">${page.startups.map((s) => renderStartup(s, preview)).join("")}</div>`
    : ""

  const footer = preview
    ? ""
    : `<div class="ms-footer"><span>Built with</span><a class="ms-footer-brand" href="${escapeAttr(opts?.origin || "https://makerstack.com")}" target="_blank" rel="noopener"><span class="ms-brand-mark" aria-hidden="true"><i></i><i></i><i></i></span><strong>MakerStack</strong></a></div>`

  return `<div class="ms-container">
  <div class="ms-profile">
    ${avatar}
    <div>
      <h1 class="ms-name">${escapeHtml(page.displayName)}</h1>
      ${location}
    </div>
    ${bio}
    ${socialsBlock}
    ${subscribe}
  </div>
  ${startups}
  ${footer}
</div>`
}
